import { Brightness1, Edit } from "@mui/icons-material";
import { Button, Grid, Paper, useTheme } from "@mui/material";
import countries from "iso3166-2-db/countryList/dispute/UN/de.json";
import { useState } from "react";
import { Form } from "react-final-form";
import { connect, ConnectedProps } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UrlPathCustomerDetails } from "../../api/url";
import {
  thunkClearCustomer,
  thunkCreateCustomerCustomField,
  thunkDeleteCustomerCustomField,
  thunkGetCustomer,
  thunkGetCustomerCustomFields,
  thunkGetCustomerFieldConfig,
  thunkUpdateCustomer,
  thunkUpdateCustomerCustomField,
} from "../../store/actions/CustomerCareActions";
import { canMaintainCustomers } from "../../store/actions/LoginActions";
import {
  thunkCancelCustomerMembership,
  thunkRevokeCancelCustomerMembership,
} from "../../store/actions/MembershipCancellationActions";
import { IStore } from "../../store/IStore";
import { CircleDto } from "../../store/models/circle/CircleDto";
import { ConfigGroupKey } from "../../store/models/config/ConfigGroupKey";
import { AddressDto } from "../../store/models/customer/AddressDto";
import { CustomerDto } from "../../store/models/customer/CustomerDto";
import {
  CustomerStatus,
  getCustomerStatusColor,
  getCustomerStatusText,
} from "../../store/models/customer/CustomerStatusDto";
import { useGenderOptions } from "../../store/models/customer/Gender";
import MenuLayout from "../app/MenuLayout";
import {
  useValidateDateOfBirth,
  useValidateEmail,
  useValidateName,
  useValidateZipCode,
  validateCustomerFields,
} from "../atoms/FieldValidation";
import { FormCheckbox } from "../atoms/FormCheckbox";
import { FormDate } from "../atoms/FormDate";
import { FormInput } from "../atoms/FormInput";
import { FormSelect } from "../atoms/FormSelect";
import { COLOR_GRAY_1, COLOR_WHITE, KNISTR_RED } from "../atoms/ImsMaterialTheme";
import ImsPaperHead from "../atoms/ImsPaperHead";
import { nameof, nameof2, ValidationErrors } from "../atoms/Utils";
import { getHeadlineText } from "./CustomerCareDetailsPage";
import UpdateCustomerStatus from "./CustomerStatusDialog";
import MembershipCancellationDialog from "./MembershipCancellationDialog";
import { FormRadioButton } from "../atoms/FormRadioButton";
import { useTranslation } from "react-i18next";
import { useCustomerTypeOptions } from "../../store/models/customer/CustomerType";

const countryList = Object.entries(countries)
  .map(([k, v]) => ({
    value: k,
    label: v.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const CustomerCareEditPage = (props: ThunkProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [redirect, setRedirect] = useState(false);
  const [updateCustomerStatusVisible, setUpdateCustomerStatusVisible] = useState(false);
  const [membershipCancellationDialogVisible, setMembershipCancellationDialogVisible] = useState(false);
  const { id } = useParams<"id">();
  const validateDateOfBirth = useValidateDateOfBirth();
  const validateName = useValidateName();
  const validateEmail = useValidateEmail();
  const validateZipCode = useValidateZipCode();
  const customerTypeOptions = useCustomerTypeOptions();
  const genderOptions = useGenderOptions();

  const { customerFieldConfig, operationalUnits, circles, customer, customerCustomFields } = props;

  const customFields = props.configGroups.find((configGroup) => configGroup.key === ConfigGroupKey.CUSTOM_FIELD);

  if (!id) return null;

  if (!customer || customer.customerNumber !== id) {
    props.thunkClearCustomer();
    props.thunkGetCustomer(id);
    return null;
  }

  if (!customerFieldConfig) {
    props.thunkGetCustomerFieldConfig();
    return null;
  }

  if (!props.customerCustomFields) {
    props.thunkGetCustomerCustomFields(customer.customerNumber);
    return null;
  }

  const validateForm = (updatedCustomer: CustomerDto) => {
    const errors: ValidationErrors<CustomerDto> = {};
    validateCustomerFields(errors, customer, updatedCustomer, customerFieldConfig);
    validateDateOfBirth(errors, updatedCustomer.dateOfBirth);
    validateName(errors, updatedCustomer.firstName, updatedCustomer.lastName);
    validateEmail(updatedCustomer.mailAddress, errors, "mailAddress");
    validateZipCode(updatedCustomer.address?.zipCode, errors, "address.zipCode");
    return errors;
  };

  const getCircleNameFromNumber = (circleNumber?: string) =>
    circles.find((circleObj: CircleDto) => circleObj.circleNumber === circleNumber)?.circleName;

  const handleCustomerMembership = async () => {
    if (customer.executionDate) {
      await props.thunkRevokeCancelCustomerMembership(customer.customerNumber);
    } else {
      setMembershipCancellationDialogVisible(true);
    }
  };

  const onSubmit = async (customer: CustomerDto) => {
    if (customer.dateOfBirth === "") {
      customer.dateOfBirth = undefined;
    }
    if (!(await props.thunkUpdateCustomer(customer))) {
      return;
    }
    let reloadCustomFields = false;
    for (let customField of customFields?.values || []) {
      const fieldName = customField.key;
      const oldValue = customerCustomFields?.find((customerCustomField) => customerCustomField.fieldName === fieldName);
      const fieldValue = (customer as any)[fieldName];

      if (oldValue && !fieldValue) {
        await props.thunkDeleteCustomerCustomField(customer.customerNumber, {
          fieldName,
          fieldValue,
        });
        reloadCustomFields = true;
      } else if (!oldValue && fieldValue) {
        await props.thunkCreateCustomerCustomField(customer.customerNumber, {
          fieldName,
          fieldValue,
        });
        reloadCustomFields = true;
      } else if (oldValue !== fieldValue) {
        await props.thunkUpdateCustomerCustomField(customer.customerNumber, {
          fieldName,
          fieldValue,
        });
        reloadCustomFields = true;
      }
    }
    if (reloadCustomFields) {
      await props.thunkGetCustomerCustomFields(customer.customerNumber);
    }
    setRedirect(true);
  };

  const getButtonText = () => {
    if (customer.executionDate) {
      return t("customerCareEditPage.buttonRevokeCancelMember");
    } else {
      return t("customerCareEditPage.buttonCancelMember");
    }
  };

  const getButtonStyling = () => {
    if (customer.executionDate) {
      return {
        backgroundColor: theme.palette.primary.main,
        color: COLOR_WHITE,
      };
    }
    return {
      backgroundColor: KNISTR_RED,
      color: COLOR_WHITE,
    };
  };

  if (redirect) {
    return <Navigate to={UrlPathCustomerDetails.replace(":id", id)} />;
  }

  const initialValues = {
    ...customer,
    ...customFields?.values?.reduce(
      (prev, customField) => ({
        ...prev,
        [customField.key]: customerCustomFields?.find(
          (customerCustomField) => customerCustomField.fieldName === customField.key
        )?.fieldValue,
      }),
      {}
    ),
  };

  const isCustomerCancelled = customer.status === CustomerStatus.CANCELLED;


  return (
    <MenuLayout headline={getHeadlineText(customer)} subHeadline={getCircleNameFromNumber(customer.circleNumber)}>
      {updateCustomerStatusVisible && (
        <UpdateCustomerStatus setCustomerStatusDialogVisible={setUpdateCustomerStatusVisible} />
      )}
      {membershipCancellationDialogVisible && (
        <MembershipCancellationDialog setMembershipCancellationDialogVisible={setMembershipCancellationDialogVisible} />
      )}
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validateForm}

        render={({ handleSubmit, submitting, initialValues }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid
                container
                style={{
                  marginBottom: theme.spacing(7.5),
                }}
              >
                <Grid item md={6} container alignItems="center">
                  <Brightness1
                    style={{
                      fill: getCustomerStatusColor(customer.status),
                      marginRight: theme.spacing(1),
                      paddingBottom: 2,
                    }}
                  />
                  {getCustomerStatusText(customer.status, t)}
                  {canMaintainCustomers() && customer.status !== CustomerStatus.CANCELLED && (
                    <Edit
                      style={{
                        marginLeft: theme.spacing(2),
                        fontSize: 24,
                        backgroundColor: theme.palette.primary.main,
                        color: COLOR_GRAY_1,
                        cursor: "hand",
                        borderRadius: "50%",
                        padding: 1,
                      }}
                      onClick={() => {
                        setUpdateCustomerStatusVisible(true);
                        return false;
                      }}
                    />
                  )}
                </Grid>
                {canMaintainCustomers() && (
                  <Grid container justifyContent="flex-end">
                    <Button
                      variant="contained"
                      style={getButtonStyling()}
                      onClick={() => {
                        handleCustomerMembership();
                      }}
                    >
                      {getButtonText()}
                    </Button>
                  </Grid>
                )}
              </Grid>

              <Paper
                style={{
                  padding: theme.spacing(3),
                  margin: theme.spacing(6, 0, 3, 0),
                }}
              >
                <ImsPaperHead text={t("customerCareEditPage.headlinePersonalInfo")} />
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormSelect
                      name={nameof<CustomerDto>("gender")}
                      label={t("customerCareEditPage.labelGender")}
                      options={genderOptions}
                      fullWidth={true}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("title")}
                      label={t("customerCareEditPage.labelTitle")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("firstName")}
                      label={t("customerCareEditPage.labelFirstname")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("lastName")}
                      label={t("customerCareEditPage.labelLastname")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      name={nameof<CustomerDto>("externalCustomerId")}
                      fullWidth={true}
                      label={t("customerCareEditPage.labelCustomerNumber")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormDate
                      name={nameof<CustomerDto>("dateOfBirth")}
                      label={t("customerCareEditPage.labelDateOfBirth")}
                      fullWidth={true}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormSelect
                      name={nameof<CustomerDto>("externalMasterUnitNumber")}
                      label={t("customerCareEditPage.labelMasterBranch")}
                      fullWidth
                      options={operationalUnits.map((operationalUnit) => ({
                        label: operationalUnit.unitName,
                        value: operationalUnit.unitNumber,
                      }))}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      name={nameof<CustomerDto>("companyName")}
                      fullWidth={true}
                      label={t("customerCareEditPage.labelCompanyName")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      name={nameof<CustomerDto>("vatId")}
                      fullWidth={true}
                      label={t("customerCareEditPage.labelVatId")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                style={{
                  padding: theme.spacing(3),
                  margin: theme.spacing(6, 0, 3, 0),
                }}
              >
                <ImsPaperHead text={t("customerCareEditPage.headlineContactInfo")} />
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof2<CustomerDto, AddressDto>("address", "streetName")}
                      label={t("customerCareEditPage.labelStreet")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={1}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof2<CustomerDto, AddressDto>("address", "houseNumber")}
                      label={t("customerCareEditPage.labelHouseNumber")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={2}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof2<CustomerDto, AddressDto>("address", "addressAddition")}
                      label={t("customerCareEditPage.labelAddressAddition")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={1}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof2<CustomerDto, AddressDto>("address", "zipCode")}
                      label={t("customerCareEditPage.labelZipCode")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={5}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof2<CustomerDto, AddressDto>("address", "cityName")}
                      label={t("customerCareEditPage.labelCity")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("mailAddress")}
                      label={t("customerCareEditPage.labelEmail")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item md={3}>
                    <FormSelect
                      name={nameof2<CustomerDto, AddressDto>("address", "country")}
                      label={t("customerCareEditPage.labelCountry")}
                      options={countryList}
                      fullWidth={true}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  style={{
                    margin: theme.spacing(0),
                  }}
                >
                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("phoneNumber")}
                      label={t("customerCareEditPage.labelPhone")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>

                  <Grid item md={3}>
                    <FormInput
                      type="text"
                      fullWidth={true}
                      name={nameof<CustomerDto>("mobileNumber")}
                      label={t("customerCareEditPage.labelMobileNumber")}
                      variant="outlined"
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {Boolean(customFields?.values) && (
                <Paper
                  style={{
                    padding: theme.spacing(3),
                    margin: theme.spacing(6, 0, 3, 0),
                  }}
                >
                  <ImsPaperHead text={t("customerCareEditPage.headlineAdditionalFields")} />
                  <Grid container spacing={2} style={{ paddingBottom: theme.spacing(4) }}>
                    {customFields?.values.map((customField) => (
                      <Grid item md={2} key={customField.key}>
                        <FormInput
                          type="text"
                          name={customField.key}
                          fullWidth={true}
                          label={customField.value}
                          variant="outlined"
                          readOnly={isCustomerCancelled}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              <Paper
                style={{
                  padding: theme.spacing(3),
                  margin: theme.spacing(6, 0, 3, 0),
                }}
              >
                <ImsPaperHead text={t("customerCareEditPage.headlineProgram")} />
                <Grid container spacing={2} style={{ paddingBottom: theme.spacing(4) }}>
                  <Grid item md={2}>
                    <FormSelect
                      name={nameof<CustomerDto>("circleNumber")}
                      label={"Circle"}
                      options={circles.map((circle) => ({
                        label: circle.circleName,
                        value: circle.circleNumber,
                      }))}
                      fullWidth={true}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <FormRadioButton
                      row
                      name={nameof<CustomerDto>("customerType")}
                      value={customer?.customerType}
                      options={customerTypeOptions}
                      fieldConfig={customerFieldConfig}
                      readOnly={isCustomerCancelled}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Grid container justifyContent="flex-end">
                <FormCheckbox
                  name={nameof<CustomerDto>("emailCorrespondenceAllowed")}
                  label={
                    !initialValues.emailCorrespondenceAllowed
                      ? t("customerCareEditPage.labelSendEmailRegisterNewsletter")
                      : t("customerCareEditPage.labelConsentNewsletter")
                  }
                  readOnly={isCustomerCancelled}
                />
              </Grid>
              <Grid container justifyContent="flex-end">
                <FormCheckbox
                  name={nameof<CustomerDto>("letterCorrespondenceAllowed")}
                  label={t("customerCareEditPage.labelLetterCorrespondenceAllowed")}
                  readOnly={isCustomerCancelled}
                />
              </Grid>
              <Grid
                container
                justifyContent="flex-end"
                style={{
                  margin: theme.spacing(1, 0, 2, 0),
                }}
              >
                <Button color="secondary" type="button" onClick={() => setRedirect(true)} variant="contained">
                  {t("common.cancel")}
                </Button>

                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  disabled={isCustomerCancelled ?? submitting}
                  style={{ marginLeft: theme.spacing(2) }}
                >
                  {t("common.save")}
                </Button>
              </Grid>
            </form>
          );
        }}
      />
    </MenuLayout>
  );
};

const mapStateToProps = (state: IStore) => ({
  customer: state.customerCare.customer,
  operationalUnits: state.operationalUnits.operationalUnits,
  customerFieldConfig: state.customerCare.customerFieldConfig,
  circles: state.circles.circles,
  configGroups: state.configGroups.configGroups,
  customerCustomFields: state.customerCare.customerCustomFields,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkClearCustomer,
      thunkGetCustomer,
      thunkUpdateCustomer,
      thunkGetCustomerFieldConfig,
      thunkCancelCustomerMembership,
      thunkRevokeCancelCustomerMembership,
      thunkGetCustomerCustomFields,
      thunkCreateCustomerCustomField,
      thunkUpdateCustomerCustomField,
      thunkDeleteCustomerCustomField,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(CustomerCareEditPage);
